import React, {useState} from "react";
import IconButton from "../../../base/button/icon_button";
import classnames from "classnames";
import Button from "../../../base/button/button";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {Fade} from "@mui/material";

const SummaryActionCard = ({header = {}, body = [], actions = [], className = '', loading, loadingCount = 4}) => {
    const [submittingEntries, setSubmittingEntries] = useState([]);
    const [submittingActions, setSubmittingActions] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Invokes the entry action's onClick callback.
     *
     * @param entry
     * @return {Promise<void>}
     */
    const submitEntryAction = async (entry) => {
        toggleSubmitting(entry.id, setSubmittingEntries);
        await entry?.action?.onClick(entry);
        if (!isMounted())
            return;
        toggleSubmitting(entry.id, setSubmittingEntries);
    }

    /**
     * Invokes the action's onClick callback.
     *
     * @param action
     * @return {Promise<void>}
     */
    const submitAction = async (action) => {
        toggleSubmitting(action.id, setSubmittingActions);
        await action?.onClick?.(action);
        if (!isMounted())
            return;
        toggleSubmitting(action.id, setSubmittingActions);
    }

    /**
     * Either appends or removes the given entry id to or from the [submittingEntries] or [submittingActions] state
     * @param {any} id
     * @param {Function} set
     */
    const toggleSubmitting = (id, set) => {
        set(prevState => {
            if (prevState?.includes(id)) {
                return prevState.filter(e => e !== id);
            }
            return [...(prevState ?? []), id];
        })
    }

    return (
        <>
            <div className={classnames('summary-action-card', className)}>
                <div className={'summary-action-card-header'}>
                    <div className={'summary-action-card-header-curve'}>
                        <div/>
                    </div>
                    <p className={'summary-action-card-header-value'}>
                        {
                            loading
                                ? header?.loadingValue ?? ''
                                : header?.format
                                    ? header?.format(header?.value) ?? ''
                                    : header?.value ?? ''
                        }
                    </p>
                    <p className={'summary-action-card-header-title'}>
                        {header?.title ?? ''}
                    </p>
                </div>
                <div className={'summary-action-card-body'}>
                    {
                        loading
                            ? Array(loadingCount).fill(null).map((_, e) => (
                                <div key={e} className={'loading extraSmall mt-1'}>
                                    <div/>
                                </div>
                            ))
                            : body?.map(entry => {
                                const submittingEntry = !!submittingEntries?.find(e => e === entry?.id);
                                return (
                                    <div key={entry.id} className={'summary-action-card-body-entry'}>
                                        <div className={'summary-action-card-body-entry-title'}>
                                            {
                                                entry?.titlePrefix
                                            }
                                            <p>
                                                {entry?.title ?? ''}
                                            </p>
                                            {
                                                entry?.titleSuffix
                                            }
                                        </div>
                                        <div className={'summary-action-card-body-entry-value'}>
                                            {
                                                !!entry?.freeFormValue &&
                                                entry.value
                                            }
                                            {
                                                !entry?.freeFormValue &&
                                                <p>
                                                    {
                                                        entry?.format
                                                            ? entry?.format(entry?.value) ?? ''
                                                            : entry?.value ?? ''
                                                    }
                                                </p>
                                            }
                                            <Fade in={entry?.action?.show} unmountOnExit mountOnEnter>
                                                <IconButton
                                                    {...(entry?.action?.props ?? {})}
                                                    disabled={submittingEntry || entry?.action?.submitting}
                                                    className={classnames('ml-2', entry?.action?.className)}
                                                    onPressed={() => submitEntryAction(entry)}
                                                >
                                                    {
                                                        !submittingEntry && !entry?.action?.submitting
                                                            ? entry?.action?.icon ?? ''
                                                            : entry?.action?.submittingIcon ?? entry?.action?.icon ?? ''
                                                    }
                                                </IconButton>
                                            </Fade>
                                        </div>
                                    </div>
                                );
                            })
                    }
                </div>
                <div className={'summary-action-card-actions'}>
                    {
                        !loading && (
                            actions
                                ?.filter(action => !!action?.show)
                                ?.map(action => {
                                    const submittingAction = !!submittingActions?.find(e => e === action?.id);
                                    return (
                                        <div
                                            key={action.id}
                                            className={classnames('summary-action-card-action-entry', action.className)}
                                        >
                                            <Button
                                                disabled={action.disabled || submittingAction}
                                                onClick={() => submitAction(action)}
                                                variant={action.variant}
                                                className={classnames({[action.variant]: true})}
                                                {...action?.props ?? {}}
                                            >
                                                {
                                                    submittingAction
                                                        ? action.submittingValue ?? ''
                                                        : action.value
                                                }
                                            </Button>
                                        </div>
                                    );
                                })
                        )
                    }
                </div>
            </div>
        </>
    )
}

export default SummaryActionCard;
